/**
 * @author michalstolarski
 * @since 2022-3-28
 */

import * as React from "react";
import styled from "styled-components";

import { rem } from "@ui/helpers";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { BottomSheetModal } from "@ui/Molecules/BottomSheetModal";

import { Color, defaultColor } from "./MobileActionMenu.helpers";
import { MobileActionMenuItemProps as ItemProps } from "./MobileActionMenuItem";

interface MobileActionMenuSettings {
  colorTheme?: Color;
  /**
   * if `false` and there is only one child,
   * the child's onClick method will be called automatically
   *
   * Default: `true`
   */
  openForSingleChild?: boolean;
}

interface Props extends MobileActionMenuSettings {
  testId?: string;
  className?: string;
  children: React.ReactNode;
  buttonStyle?: React.CSSProperties;
  withIcons?: boolean;
  translations: {
    buttonText: string;
  };
  onButtonClick?: () => void;
  onClose: () => void;
}

/**
 * MobileActionMenu automatically passes `colorTheme` and` withIcons` properties to the children
 */
const MobileActionMenu = ({
  colorTheme = defaultColor,
  openForSingleChild = true,
  ...props
}: Props): React.ReactElement => {
  const [forcedClosing, setForcedClosing] = React.useState(false);

  const onButtonClick = () => {
    setForcedClosing(true);

    props.onButtonClick?.();
  };

  React.useEffect(() => {
    const children = React.Children.toArray(props.children);

    if (
      openForSingleChild ||
      children.length !== 1 ||
      !React.isValidElement(children[0])
    )
      return;

    children[0].props.onClick?.();
    setForcedClosing(true);
  }, []);

  return (
    <TestWrapper testId={props.testId}>
      <BottomSheetModal
        forcedClosing={forcedClosing}
        setForcedClosing={setForcedClosing}
        onClose={props.onClose}
      >
        <ActionMenuList className={props.className}>
          {React.Children.map(props.children, (child, i) => {
            if (!React.isValidElement<ItemProps>(child)) {
              return child;
            }

            return (
              <React.Fragment key={i}>
                {React.cloneElement(child, {
                  iconColor: child.props.iconColor || colorTheme,
                  isIconEnabled: props.withIcons,
                  onClick: () => {
                    // close without animation
                    // important for modal mechanism
                    props.onClose();
                    child.props.onClick();
                  },
                })}
              </React.Fragment>
            );
          })}
        </ActionMenuList>

        <Button
          color={colorTheme || defaultColor}
          style={props.buttonStyle}
          onClick={onButtonClick}
        >
          {props.translations.buttonText}
        </Button>
      </BottomSheetModal>
    </TestWrapper>
  );
};

export { MobileActionMenu };

const ActionMenuList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Button = styled.button<{ color: Color }>`
  min-height: ${rem(48)};
  width: 100%;
  padding: ${({ theme }) =>
    `${theme.margins.base_x1_5} ${theme.margins.base_x3}`};
  font-weight: ${props => props.theme.fonts.weights.bold};
  text-align: center;
  color: ${props => props.theme.palette[props.color]};

  &:hover {
    background-color: ${props => props.theme.palette.grey50};
  }
`;
